import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getCommunityItemById,
  updateCommunityPost,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../../components/layout";
import { Phone, Mail, Save, AlertCircle } from "lucide-react";
import type { CommunitySectionConfig } from "../types";

function getListPath(
  section: CommunitySectionConfig,
  locationCode?: string | null,
) {
  const base = `/${section.path}`;
  return locationCode ? `/locations/${locationCode}${base}` : base;
}

export interface CommunityEditPageProps {
  section: CommunitySectionConfig;
}

export default function CommunityEditPage({ section }: CommunityEditPageProps) {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const updateFn = useAction(updateCommunityPost);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getCommunityItemById, {
    itemId: itemId!,
    contentType: section.contentType,
  });

  useEffect(() => {
    if (item) {
      const meta = (item.metadata || {}) as {
        contactPhone?: string;
        contactEmail?: string;
        contactInfo?: string;
      };
      const hasNewContact = meta.contactPhone || meta.contactEmail;
      setTitle(item.title || "");
      setDescription(item.description || "");
      setContactPhone(meta.contactPhone || "");
      setContactEmail(
        meta.contactEmail ||
        (!hasNewContact && meta.contactInfo ? meta.contactInfo : "") ||
        "",
      );
    }
  }, [item]);

  const listPath = getListPath(section, selectedLocation?.locationCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrors((prev) => ({ ...prev, title: "Title is required" }));
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      await updateFn({
        itemId: itemId!,
        contentType: section.contentType,
        title: title.trim(),
        description: description.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      });
      navigate(`${listPath}/${itemId}`);
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to update post. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate(`${listPath}/${itemId}`);

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to edit posts.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Post...">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading post details...</p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (error || !item) {
    return (
      <IndividualEditPageLayout title="Post Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Post Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The post you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate(listPath)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (item.authorId !== user.id) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You can only edit your own posts.</p>
          <button
            onClick={() => navigate(listPath)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="community-edit-form"
        disabled={isSubmitting || !title.trim()}
        icon={Save}
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleCancel} disabled={isSubmitting}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Information" hideOnMobile>
      <p className="text-sm text-slate-500">
        Review your post details carefully before saving. Changes will be visible
        to the community immediately.
      </p>
    </SidebarSection>
  );

  return (
    <IndividualEditPageLayout
      title={`Edit ${section.label} Post`}
      subtitle={`Location: ${selectedLocation?.name || "—"}`}
      icon={section.icon}
      actions={actionsContent}
      sidebar={sidebarContent}
    >

      <form
        id="community-edit-form"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring ${errors.title
              ? "border-destructive bg-destructive/10"
              : "border-input"
              }`}
            placeholder="Brief title"
            required
          />
          {errors.title && (
            <p className="mt-1 text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.title}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring"
            placeholder="Details..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring"
              placeholder="Phone (optional)"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring"
              placeholder="Email (optional)"
            />
          </div>
        </div>

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
